import { useCallback, useMemo } from 'react';
import { parseISO } from 'date-fns/parseISO';
import { getIn, useFormikContext } from 'formik';

import { DatePicker } from '@mui/x-date-pickers';
import Grid from '@mui/material/Grid';

import { EMPTY_OBJECT } from '@e-socna/empty';
import type { EMPTY_STRING } from '@e-socna/empty';
import type {
  PaymentMethodId,
  PaymentTermId,
  ValidityPeriodId,
} from '@e-socna/data-layer';

import { useContextualizedFields } from '../../hooks/useContextualizedFields.hook';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { LazyEditor } from '../../components/Editor/Lazy';
import { IdSelect } from '../../components/IdSelect';
import type { FileStorage } from '../../components/Editor/types/file-storage.type';
import type { DocumentResolversSettings } from '../../components/Editor/types/document-resolvers-settings.type';
import type { IdOption } from '../../components/IdSelect';

import type { QuotationHeaderForm_QuotationHeader } from './fragments/quotation-header.fragment';

type ContextualizedFields = Readonly<
  Required<
    Pick<
      QuotationHeaderForm_QuotationHeader,
      | '_id'
      | 'paymentMethodId'
      | 'paymentTermId'
      | 'validityPeriodId'
      | 'modalities'
      | 'note'
      | 'interventionDate'
    >
  >
>;

interface Props {
  readonly formContext?: string;
  readonly id: string;
  readonly resolversSettings: DocumentResolversSettings;
  readonly storage: FileStorage;
}

export const QuotationHeaderForm = ({
  formContext,
  resolversSettings,
  storage,
}: Readonly<Props>) => {
  // data needed in form
  const { paymentMethods, paymentTerms, validityPeriods } =
    useReferencesContext();

  const basicFields = useContextualizedFields<
    Omit<ContextualizedFields, 'modalities' | 'note'>
  >(
    {
      _id: {
        id: 'quotation-header-id',
        name: '_id',
      },
      paymentMethodId: {
        id: 'quotation-header-payment-method-id',
        name: 'paymentMethodId',
      },
      paymentTermId: {
        id: 'quotation-header-payment-term-id',
        name: 'paymentTermId',
      },
      validityPeriodId: {
        id: 'quotation-header-validity-period-id',
        name: 'validityPeriodId',
      },
      interventionDate: {
        id: 'quotation-header-intervention-date-id',
        name: 'interventionDate',
      },
    },
    formContext,
  );

  const editableFields = useContextualizedFields<
    Pick<ContextualizedFields, 'modalities' | 'note'>
  >(
    {
      modalities: {
        id: `${basicFields._id.value}-modalities`,
        name: 'modalities',
      },
      note: {
        id: `${basicFields._id.value}-note`,
        name: 'note',
      },
    },
    formContext,
  );

  const fields = {
    ...basicFields,
    ...editableFields,
  };

  const {
    errors,
    handleBlur,
    handleChange,
    initialValues,
    isSubmitting,
    setFieldTouched,
    setFieldValue,
    touched,
  } = useFormikContext<QuotationHeaderForm_QuotationHeader>();

  const handleInterventionDateChange = useCallback(
    (date: unknown) => void setFieldValue(fields.interventionDate.name, date),
    [fields.interventionDate.name, setFieldValue],
  );

  const handleInterventionDateBlur = useCallback(
    () => void setFieldTouched(fields.interventionDate.name, true),
    [fields.interventionDate.name, setFieldTouched],
  );

  const handlePaymentMethodIdChange = useCallback(
    (id: PaymentMethodId | typeof EMPTY_STRING) => {
      void setFieldValue(fields.paymentMethodId.name, id);
    },
    [fields.paymentMethodId.name, setFieldValue],
  );

  const handlePaymentTermIdChange = useCallback(
    (id: PaymentTermId | typeof EMPTY_STRING) => {
      void setFieldValue(fields.paymentTermId.name, id);
    },
    [fields.paymentTermId.name, setFieldValue],
  );

  const handleValidityPeriodIdChange = useCallback(
    (id: ValidityPeriodId | typeof EMPTY_STRING) => {
      void setFieldValue(fields.validityPeriodId.name, id);
    },
    [fields.validityPeriodId.name, setFieldValue],
  );

  const interventionDateValue = useMemo(
    () =>
      typeof fields.interventionDate.value === 'string'
        ? parseISO(fields.interventionDate.value)
        : fields.interventionDate.value,
    [fields.interventionDate.value],
  );

  return (
    <Grid container spacing={2}>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdSelect<PaymentMethodId, IdOption<PaymentMethodId>>
          error={
            !!(
              getIn(errors, fields.paymentMethodId.name) &&
              getIn(touched, fields.paymentMethodId.name)
            )
          }
          helperText={
            !!(
              getIn(errors, fields.paymentMethodId.name) &&
              getIn(touched, fields.paymentMethodId.name)
            ) && (getIn(errors, fields.paymentMethodId.name) as string)
          }
          id={fields.paymentMethodId.id}
          label="Moyen de Paiement"
          name={fields.paymentMethodId.name}
          onBlur={handleBlur}
          onChange={handlePaymentMethodIdChange}
          options={paymentMethods.map((paymentMethod) => ({
            id: paymentMethod._id,
            label: paymentMethod.label,
          }))}
          required
          value={fields.paymentMethodId.value as PaymentMethodId}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdSelect<PaymentTermId, IdOption<PaymentTermId>>
          error={
            !!(
              getIn(errors, fields.paymentTermId.name) &&
              getIn(touched, fields.paymentTermId.name)
            )
          }
          helperText={
            !!(
              getIn(errors, fields.paymentTermId.name) &&
              getIn(touched, fields.paymentTermId.name)
            ) && (getIn(errors, fields.paymentTermId.name) as string)
          }
          id={fields.paymentTermId.id}
          label="Conditions de Réglement"
          name={fields.paymentTermId.name}
          onBlur={handleBlur}
          onChange={handlePaymentTermIdChange}
          options={paymentTerms.map((paymentTerm) => ({
            id: paymentTerm._id,
            label: paymentTerm.label,
          }))}
          required
          value={fields.paymentTermId.value as PaymentTermId}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdSelect<ValidityPeriodId, IdOption<ValidityPeriodId>>
          error={
            !!(
              getIn(errors, fields.validityPeriodId.name) &&
              getIn(touched, fields.validityPeriodId.name)
            )
          }
          helperText={
            !!(
              getIn(errors, fields.validityPeriodId.name) &&
              getIn(touched, fields.validityPeriodId.name)
            ) && (getIn(errors, fields.validityPeriodId.name) as string)
          }
          id={fields.validityPeriodId.id}
          label="Durée de Validité du Devis"
          name={fields.validityPeriodId.name}
          onBlur={handleBlur}
          onChange={handleValidityPeriodIdChange}
          options={validityPeriods.map((validityPeriod) => ({
            id: validityPeriod._id,
            label: validityPeriod.label,
          }))}
          required
          value={fields.validityPeriodId.value as ValidityPeriodId}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <DatePicker
          displayWeekNumber
          label="Date d'intervention"
          // onError={console.log}
          onChange={handleInterventionDateChange}
          showDaysOutsideCurrentMonth
          slotProps={{
            field: {
              clearable: true,
              // onClear: () => void setFieldValue(fields.interventionDate.name, null),
            },
            actionBar: {
              // actions: ['accept', 'cancel', 'clear', 'today'],
              actions: ['cancel', 'clear', 'today'],
            },
            // toolbar: {
            //   hidden: false,
            // },
            textField: {
              error: !!(
                getIn(errors, fields.interventionDate.name) &&
                getIn(touched, fields.interventionDate.name)
              ),
              fullWidth: true,
              helperText: getIn(errors, fields.interventionDate.name) as string,
              id: fields.interventionDate.id,
              name: fields.interventionDate.name,
              onBlur: handleInterventionDateBlur,
            },
          }}
          value={interventionDateValue}
          views={['year', 'month', 'day']}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <LazyEditor
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(getIn(errors, fields.modalities.name)
            ? {
                alert: {
                  severity: 'error',
                  content: getIn(errors, fields.modalities.name) as string,
                },
              }
            : EMPTY_OBJECT)}
          disabled={isSubmitting}
          error={
            !!(
              getIn(errors, fields.modalities.name) &&
              getIn(touched, fields.modalities.name)
            )
          }
          id={fields.modalities.id}
          initialValue={getIn(initialValues, fields.modalities.name) as string}
          label="Modalités"
          name={fields.modalities.name}
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          resolversSettings={resolversSettings}
          storage={storage}
          value={fields.modalities.value}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <LazyEditor
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...(getIn(errors, fields.note.name)
            ? {
                alert: {
                  severity: 'error',
                  content: getIn(errors, fields.note.name) as string,
                },
              }
            : EMPTY_OBJECT)}
          disabled={isSubmitting}
          error={
            !!(
              getIn(errors, fields.note.name) &&
              getIn(touched, fields.note.name)
            )
          }
          id={fields.note.id}
          initialValue={getIn(initialValues, fields.note.name) as string}
          label="Nota"
          name={fields.note.name}
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          resolversSettings={resolversSettings}
          storage={storage}
          value={fields.note.value}
        />
      </Grid>
    </Grid>
  );
};
